import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Modal,
    Form,
    Input,
    InputNumber,
    Select,
    Radio,
    Button,
    Card,
    Row,
    Col,
    Typography,
    Space,
    Switch,
    Spin,
    notification,
    message
} from 'antd';
import {
    FileProtectOutlined,
    FilePdfOutlined,
    BankOutlined,
    DollarOutlined,
    EyeOutlined,
    CloseOutlined,
    SendOutlined,
    ThunderboltOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { stateGlobal } from '../../../interfaces/Global';
import { IConfigPlano, IConfiguracaoGeralProposta, IProposta, ISlidePropostaData } from '../interface';
import ConfigPlanoPropostaService from '../../../services/http/services/Propostas/ConfigPlanoPropostaService';
import ConfiguracaoGeralPropostaService from '../../../services/http/services/Propostas/ConfiguracaoGeralPropostaService';
import PropostaService from '../../../services/http/services/Propostas/PropostaService';
import { getUsuario } from '../../../services/token/usuario';
import { getToken } from '../../../services/token';
import { urlBuscaCidades } from '../../../services/request/urls';
import { EmitirErro } from '../../../services/EmitirErro';
import { VisualizadorPropostaModal } from './VisualizadorPropostaModal';
import { PropostaPrintDocument } from './PropostaPrintDocument';
import { gerarPdfPropostaDireto } from '../utils/gerarPdfProposta';
import { TEXTO_PADRAO_DETALHAMENTO_HORAS } from '../CadastrarProposta/useCadastrarProposta';

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

export interface ModalCriarPropostaProps {
    visible?: boolean;
    open?: boolean;
    onClose?: () => void;
    onCancel?: () => void;
    setFecharModal?: (visible: boolean) => void;
    onSuccess?: (proposta?: any) => void;
}

export interface ICidadeItem {
    id: number;
    nome: string;
    uf?: string;
    estado?: {
        id: number;
        nome: string;
        uf?: string;
        sigla?: string;
    };
    microrregiao?: {
        mesorregiao?: {
            estado?: {
                id: number;
                sigla: string;
                nome: string;
                uf?: string;
            };
        };
    };
}

const TEXTO_PADRAO_PAGAMENTO: Record<string, string> = {
    parcelado: 'Parcelado em parcelas mensais.',
    parcela_unica: 'No prazo máximo de 07 (sete) dias após a emissão da Nota Fiscal em parcela única.'
};

const formatarMoeda = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
};

const normalizarTexto = (texto: any): string => {
    return String(texto || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
};

const getCidadeUf = (c: ICidadeItem | any): string => {
    return (
        c?.uf ||
        c?.estado?.uf ||
        c?.estado?.sigla ||
        c?.microrregiao?.mesorregiao?.estado?.sigla ||
        c?.microrregiao?.mesorregiao?.estado?.uf ||
        ''
    ).toUpperCase();
};

export const ModalCriarProposta: React.FC<ModalCriarPropostaProps> = ({
    visible,
    open,
    onClose,
    onCancel,
    setFecharModal,
    onSuccess
}) => {
    const isModalVisible = visible !== undefined ? visible : Boolean(open);
    const isDark = useSelector((state: stateGlobal) => state?.global?.theme === 'dark');
    const loggedUser = useMemo(() => getUsuario(), []);

    const [form] = Form.useForm();
    const printRef = useRef<HTMLDivElement>(null);

    const [cidades, setCidades] = useState<ICidadeItem[]>([]);
    const [configPlanos, setConfigPlanos] = useState<IConfigPlano[]>([]);
    const configPlanoId = Form.useWatch('config_plano_proposta_comercial_id', form);
    const [configGeral, setConfigGeral] = useState<IConfiguracaoGeralProposta | null>(null);

    const [loadingInicial, setLoadingInicial] = useState<boolean>(false);
    const [loadingCidades, setLoadingCidades] = useState<boolean>(false);
    const [loadingPlanos, setLoadingPlanos] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [previewVisible, setPreviewVisible] = useState<boolean>(false);
    const [desabilitarSelecaoMunicipio, setDesabilitarSelecaoMunicipio] = useState<boolean>(false);

    const [quantidadeLicencas, setQuantidadeLicencas] = useState<number>(1);
    const [quantidadeHoras, setQuantidadeHoras] = useState<number>(0);
    const [propostaParaDownload, setPropostaParaDownload] = useState<ISlidePropostaData | null>(null);

    const selectedConfigPlano = useMemo(() => {
        if (!configPlanoId) return configPlanos.find(c => c.is_principal) || configPlanos[0] || null;
        return configPlanos.find(c => c.id === configPlanoId) || configPlanos[0] || null;
    }, [configPlanoId, configPlanos]);

    const handleClose = useCallback(() => {
        if (onClose) onClose();
        if (onCancel) onCancel();
        if (setFecharModal) setFecharModal(false);
    }, [onClose, onCancel, setFecharModal]);

    const carregarCidades = useCallback(async () => {
        try {
            setLoadingCidades(true);
            const token = getToken();
            const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
            const response = await axios.get(urlBuscaCidades, { headers });
            const lista = Array.isArray(response.data) ? response.data : [];
            lista.sort((a: ICidadeItem, b: ICidadeItem) => a.nome.localeCompare(b.nome));
            setCidades(lista);
            return lista;
        } catch (error) {
            console.error('Erro ao carregar lista de municípios:', error);
            return [];
        } finally {
            setLoadingCidades(false);
        }
    }, []);

    const carregarConfigPlanos = useCallback(async () => {
        try {
            setLoadingPlanos(true);
            const data = await ConfigPlanoPropostaService.getAll();
            setConfigPlanos(data);
            if (data.length > 0) {
                const planoPadrao = data.find(c => c.is_principal) || data[0];
                form.setFieldsValue({
                    config_plano_proposta_comercial_id: planoPadrao.id
                });
            }
            return data;
        } catch (error) {
            EmitirErro('Erro ao carregar modelos de planos');
            console.error(error);
            return [];
        } finally {
            setLoadingPlanos(false);
        }
    }, [form]);

    const carregarConfigGeral = useCallback(async () => {
        try {
            const data = await ConfiguracaoGeralPropostaService.get();
            setConfigGeral(data);
            return data;
        } catch (error) {
            console.error('Erro ao carregar configurações gerais:', error);
            return null;
        }
    }, []);

    useEffect(() => {
        if (isModalVisible) {
            form.resetFields();
            setQuantidadeLicencas(1);
            setQuantidadeHoras(0);
            setDesabilitarSelecaoMunicipio(false);

            form.setFieldsValue({
                titulo_cabecalho: 'PROPOSTA COMERCIAL AO MUNICÍPIO DE',
                quantidade_licencas: 1,
                quantidade_horas: 0,
                condicao_pagamento: 'parcela_unica'
            });

            setLoadingInicial(true);
            Promise.all([
                carregarCidades(),
                carregarConfigPlanos(),
                carregarConfigGeral()
            ]).finally(() => {
                setLoadingInicial(false);
            });
        }
    }, [isModalVisible, form, carregarCidades, carregarConfigPlanos, carregarConfigGeral]);

    const handlePlanoChange = (configId: number) => {
        form.setFieldsValue({ config_plano_proposta_comercial_id: configId });
    };

    const handleToggleDesabilitarMunicipio = (checked: boolean) => {
        setDesabilitarSelecaoMunicipio(checked);
        const currentTitulo = form.getFieldValue('titulo_cabecalho');
        form.resetFields(['cidade_id', 'cidade_nome', 'cidade_uf']);
        form.setFieldsValue({
            cidade_id: undefined,
            cidade_nome: '',
            cidade_uf: checked ? '' : undefined,
            titulo_cabecalho: currentTitulo || 'PROPOSTA COMERCIAL AO MUNICÍPIO DE'
        });
    };

    const handleCidadeSelect = (cidadeId?: number) => {
        if (!cidadeId) {
            form.resetFields(['cidade_id', 'cidade_nome', 'cidade_uf']);
            form.setFieldsValue({
                cidade_id: undefined,
                cidade_nome: '',
                cidade_uf: ''
            });
            return;
        }
        const selected = cidades.find(c => c.id === cidadeId);
        if (selected) {
            const uf = getCidadeUf(selected);
            form.setFieldsValue({
                cidade_id: selected.id,
                cidade_nome: selected.nome,
                cidade_uf: uf
            });
        }
    };

    const valorUnitarioPlano = Number(selectedConfigPlano?.plano?.valor || 0);
    const valorTotalLicencas = quantidadeLicencas * valorUnitarioPlano;

    const contratacaoHoras = Boolean(selectedConfigPlano?.contratacao_horas);
    const valorUnitarioHora = contratacaoHoras
        ? Number(configGeral?.valor_hora_padrao ?? 150)
        : 0;
    const valorTotalHoras = contratacaoHoras ? quantidadeHoras * valorUnitarioHora : 0;
    const valorTotalGeral = valorTotalLicencas + valorTotalHoras;

    const condicaoPagamentoModal = Form.useWatch('condicao_pagamento', form) || 'parcela_unica';
    const numeroParcelasModal = Form.useWatch('numero_parcelas', form) || 12;
    // A parcela mensal é calculada apenas sobre o valor das licenças (horas sob demanda são faturadas à parte)
    const valorParcelaModal = valorTotalLicencas > 0 ? valorTotalLicencas / (Number(numeroParcelasModal) || 12) : 0;

    useEffect(() => {
        if (condicaoPagamentoModal === 'parcelado' && !form.getFieldValue('numero_parcelas')) {
            form.setFieldValue('numero_parcelas', 12);
        }
    }, [condicaoPagamentoModal, form]);

    useEffect(() => {
        if (contratacaoHoras && quantidadeHoras > 0) {
            const atual = form.getFieldValue('detalhamento_horas');
            if (!atual || !atual.trim()) {
                form.setFieldValue('detalhamento_horas', TEXTO_PADRAO_DETALHAMENTO_HORAS);
            }
        } else {
            if (form.getFieldValue('detalhamento_horas')) {
                form.setFieldValue('detalhamento_horas', undefined);
            }
        }
    }, [contratacaoHoras, quantidadeHoras, form]);

    const getPreviewData = useCallback((propostaId?: number): ISlidePropostaData => {
        const values = form.getFieldsValue();
        const uf = desabilitarSelecaoMunicipio ? '' : (values.cidade_uf || '');
        const muni = values.cidade_nome || 'Município';

        const getCargoDesc = (cargo: any): string => {
            if (!cargo) return '';
            if (typeof cargo === 'object' && cargo.nome) return String(cargo.nome);
            if (typeof cargo === 'string') return cargo;
            return '';
        };

        const vendedorNome = loggedUser?.nome || 'Consultor Comercial';
        const vendedorCargo = getCargoDesc(loggedUser?.cargo) || '';
        const vendedorTelefone = loggedUser?.celular || '';
        const vendedorEmail = loggedUser?.email || '';

        const contatoPrincipalNome = configGeral?.contato_padrao?.nome || vendedorNome;
        const contatoPrincipalCargo = getCargoDesc(configGeral?.contato_padrao?.cargo) || vendedorCargo;
        const contatoPrincipalTelefone = configGeral?.contato_padrao?.celular || vendedorTelefone;
        const contatoPrincipalEmail = configGeral?.contato_padrao?.email || vendedorEmail;

        const condicao = values.condicao_pagamento || 'parcela_unica';
        const parcelas = condicao === 'parcelado' ? Number(values.numero_parcelas || 12) : 1;
        const valorParcela = valorTotalLicencas > 0 ? valorTotalLicencas / parcelas : 0;
        let formaDescricao: string;
        if (condicao === 'parcelado') {
            formaDescricao = contratacaoHoras && quantidadeHoras > 0
                ? `Licenciamento parcelado em ${parcelas}x de ${formatarMoeda(valorParcela)} mensais. Horas técnicas faturadas sob demanda conforme ordem de serviço e horas executadas.`
                : `Parcelado em ${parcelas}x de ${formatarMoeda(valorParcela)} mensais.`;
        } else {
            formaDescricao = contratacaoHoras && quantidadeHoras > 0
                ? `Licenciamento em parcela única no prazo de até 07 dias após a emissão da NF. Horas técnicas faturadas sob demanda conforme ordem de serviço e horas executadas.`
                : TEXTO_PADRAO_PAGAMENTO.parcela_unica;
        }

        return {
            id: propostaId,
            titulo_cabecalho: values.titulo_cabecalho || 'PROPOSTA COMERCIAL AO MUNICÍPIO DE',
            cidade_nome: muni,
            cidade_uf: uf,
            cidade_completo: uf ? `${muni} - ${uf}` : muni,
            plano_nome: selectedConfigPlano?.plano?.nome || selectedConfigPlano?.descricao_plano || 'Licença Intermediária',
            descricao_plano: selectedConfigPlano?.descricao_plano,
            texto_objeto: selectedConfigPlano?.texto_objeto_padrao,
            quantidade_licencas: quantidadeLicencas,
            valor_unitario_plano: valorUnitarioPlano,
            valor_total_plano: valorTotalLicencas,
            contratacao_horas: contratacaoHoras,
            quantidade_horas: contratacaoHoras ? quantidadeHoras : null,
            valor_unitario_horas: contratacaoHoras ? valorUnitarioHora : null,
            valor_total_horas: contratacaoHoras ? valorTotalHoras : null,
            valor_total_geral: valorTotalGeral,
            condicao_pagamento: condicao,
            forma_pagamento_descricao: formaDescricao,
            detalhamento: selectedConfigPlano?.detalhamento_padrao,
            detalhamento_horas: (contratacaoHoras && quantidadeHoras > 0)
                ? (form.getFieldValue('detalhamento_horas') || TEXTO_PADRAO_DETALHAMENTO_HORAS)
                : null,
            possui_tela: selectedConfigPlano?.possui_tela ?? true,
            tipo_tela: selectedConfigPlano?.tipo_tela || (selectedConfigPlano?.possui_tela ? 'tela' : null),
            texto_dispositivo_padrao: selectedConfigPlano?.texto_dispositivo_padrao,
            gerado_em: new Date(),
            gerado_por_nome: loggedUser?.nome || 'Consultor Comercial',
            vendedor_nome: vendedorNome,
            vendedor_cargo: vendedorCargo,
            vendedor_telefone: vendedorTelefone,
            vendedor_email: vendedorEmail,
            contato_principal_nome: contatoPrincipalNome,
            contato_principal_cargo: contatoPrincipalCargo,
            contato_principal_telefone: contatoPrincipalTelefone,
            contato_principal_email: contatoPrincipalEmail,
            municipios_destaque: configGeral?.municipios_destaque,
            gerada: true
        };
    }, [
        form,
        desabilitarSelecaoMunicipio,
        loggedUser,
        configGeral,
        selectedConfigPlano,
        quantidadeLicencas,
        valorUnitarioPlano,
        valorTotalLicencas,
        contratacaoHoras,
        quantidadeHoras,
        valorUnitarioHora,
        valorTotalHoras,
        valorTotalGeral
    ]);

    const handleOpenPreview = async () => {
        try {
            await form.validateFields();
            setPreviewVisible(true);
        } catch (error) {
            message.warning('Por favor, preencha os campos obrigatórios antes de pré-visualizar.');
        }
    };

    const onFinish = async (values: any) => {
        setSubmitting(true);
        try {
            const condicao = values.condicao_pagamento || 'parcela_unica';
            const parcelas = condicao === 'parcelado' ? Number(values.numero_parcelas || 12) : 1;
            const valorParcela = valorTotalGeral > 0 ? valorTotalGeral / parcelas : 0;
            const formaDescricao = condicao === 'parcelado'
                ? `Parcelado em ${parcelas}x de ${formatarMoeda(valorParcela)} mensais.`
                : TEXTO_PADRAO_PAGAMENTO.parcela_unica;

            const payload = {
                config_plano_proposta_comercial_id: values.config_plano_proposta_comercial_id,
                cidade_nome: values.cidade_nome,
                cidade_uf: desabilitarSelecaoMunicipio ? '' : (values.cidade_uf || ''),
                titulo_cabecalho: values.titulo_cabecalho || 'PROPOSTA COMERCIAL AO MUNICÍPIO DE',
                vendedor_id: loggedUser?.id || null,
                contato_principal_id: configGeral?.contato_padrao_id || loggedUser?.id || null,
                quantidade_licencas: Number(values.quantidade_licencas || 1),
                quantidade_horas: contratacaoHoras ? Number(values.quantidade_horas || 0) : null,
                condicao_pagamento: condicao,
                numero_parcelas: parcelas,
                forma_pagamento_descricao: formaDescricao,
                valor_total_licenca: valorTotalLicencas,
                valor_total_hora: contratacaoHoras ? valorTotalHoras : null,
                detalhamento_horas: (contratacaoHoras && quantidadeHoras > 0)
                    ? (values.detalhamento_horas || form.getFieldValue('detalhamento_horas') || TEXTO_PADRAO_DETALHAMENTO_HORAS)
                    : null,
                enviado_para: values.enviado_para && values.enviado_para.trim() ? values.enviado_para.trim() : undefined,
                enviado_em: values.enviado_para && values.enviado_para.trim() ? new Date() : undefined
            };

            const response = await PropostaService.createProposta(payload);

            notification.success({
                message: "Sucesso!",
                description: 'Proposta criada com sucesso!'
            });

            const slideDataComId = getPreviewData(response?.id);
            setPropostaParaDownload(slideDataComId);

            // Aguarda o ciclo de renderização do DOM com o ID preenchido
            await new Promise(resolve => setTimeout(resolve, 350));

            if (printRef.current) {
                const muni = (values.cidade_nome || 'Municipio').replace(/\s+/g, '_');
                const uf = values.cidade_uf ? `_${values.cidade_uf.toUpperCase()}` : '';
                const docTitle = `Proposta_Comercial_${muni}${uf}_GovFacil`;

                message.loading({ content: 'Proposta salva! Gerando e baixando PDF...', key: 'pdf_save_download', duration: 0 });
                try {
                    await gerarPdfPropostaDireto({
                        containerElement: printRef.current,
                        documentTitle: docTitle,
                        onProgress: (current, total) => {
                            message.loading({
                                content: `Gerando PDF da proposta (${current} de ${total})...`,
                                key: 'pdf_save_download',
                                duration: 0
                            });
                        }
                    });
                    message.success({ content: 'Proposta salva e PDF baixado com sucesso!', key: 'pdf_save_download', duration: 3 });
                } catch (pdfError) {
                    console.error('Erro ao gerar PDF automaticamente:', pdfError);
                    message.warning({ content: 'Proposta salva! Não foi possível gerar o download automático do PDF.', key: 'pdf_save_download', duration: 4 });
                }
            }

            if (onSuccess) {
                onSuccess(response);
            }
            handleClose();
        } catch (error: any) {
            EmitirErro(error);
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Modal
                title={
                    <Space align="center" style={{ fontSize: '17px', fontWeight: 600 }}>
                        <span>Nova Proposta Comercial Rápida</span>
                    </Space>
                }
                visible={isModalVisible}
                onCancel={handleClose}
                width={780}
                style={{ maxWidth: 'calc(100vw - 16px)', top: 12, paddingBottom: 12 }}
                bodyStyle={{ maxHeight: 'calc(100vh - 160px)', overflowY: 'auto', padding: '16px 12px' }}
                destroyOnClose
                maskClosable={true}
                footer={
                    <div style={{ width: '100%' }}>
                        <Row gutter={[8, 8]} align="middle">
                            <Col xs={12} sm={6} md="auto">
                                <Button block onClick={handleClose}>
                                    Cancelar
                                </Button>
                            </Col>
                            <Col xs={12} sm={8} md="auto">
                                <Button
                                    block
                                    icon={<EyeOutlined />}
                                    onClick={handleOpenPreview}
                                    disabled={submitting}
                                >
                                    Pré-visualizar
                                </Button>
                            </Col>
                            <Col xs={24} sm={10} md="auto" style={{ marginLeft: 'auto' }}>
                                <Button
                                    type="primary"
                                    icon={<FilePdfOutlined />}
                                    loading={submitting}
                                    block
                                    onClick={() => form.submit()}
                                    style={{
                                        background: '#0066b2',
                                        borderColor: '#0066b2',
                                        fontWeight: 600,
                                        height: 38,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    Salvar e Baixar Proposta (PDF)
                                </Button>
                            </Col>
                        </Row>
                    </div>
                }
            >
                <Spin spinning={loadingInicial} tip="Carregando dados da proposta...">
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        initialValues={{
                            titulo_cabecalho: 'PROPOSTA COMERCIAL AO MUNICÍPIO DE',
                            quantidade_licencas: 1,
                            quantidade_horas: 0,
                            condicao_pagamento: 'parcela_unica'
                        }}
                    >
                        {/* Campos Ocultos para Sincronização */}
                        {!desabilitarSelecaoMunicipio && (
                            <Form.Item name="cidade_nome" hidden>
                                <Input />
                            </Form.Item>
                        )}
                        <Form.Item name="cidade_uf" hidden>
                            <Input />
                        </Form.Item>

                        {/* Bloco 1: Identificação */}
                        <Card
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    marginBottom: 16
                                }}
                            >
                                <Text strong>Desabilitar seleção de município</Text>
                                <Switch
                                    size="small"
                                    checked={desabilitarSelecaoMunicipio}
                                    onChange={handleToggleDesabilitarMunicipio}
                                    style={{ minWidth: 32 }}
                                />
                            </div>

                            {desabilitarSelecaoMunicipio && (
                                <Form.Item
                                    name="titulo_cabecalho"
                                    label="Título do Cabeçalho da Proposta"
                                    rules={[{ required: true, message: 'Por favor, informe o título do cabeçalho' }]}
                                >
                                <Input
                                    placeholder="Ex: PROPOSTA COMERCIAL AO MUNICÍPIO DE"
                                />
                                </Form.Item>
                            )}

                            {desabilitarSelecaoMunicipio ? (
                                <Form.Item
                                    name="cidade_nome"
                                    label="Associação / Destinatário"
                                    rules={[{ required: true, message: 'Por favor, informe a identificação ou município' }]}
                                >
                                    <Input
                                        placeholder="Ex: CONSÓRCIO INTERMUNICIPAL DE SAÚDE ou CIDADE EXEMPLO"
                                    />
                                </Form.Item>
                            ) : (
                                <Form.Item
                                    name="cidade_id"
                                    label="Município"
                                    rules={[{ required: true, message: 'Selecione o município' }]}
                                >
                                    <Select
                                        showSearch
                                        allowClear
                                        loading={loadingCidades}
                                        placeholder="Digite para buscar qualquer município do Brasil..."
                                        optionFilterProp="children"
                                        filterOption={(input, option: any) =>
                                            normalizarTexto(option?.children).includes(normalizarTexto(input))
                                        }
                                        onChange={handleCidadeSelect}
                                        notFoundContent={loadingCidades ? "Carregando municípios..." : "Nenhum município encontrado"}
                                    >
                                        {cidades.map(c => {
                                            const uf = getCidadeUf(c);
                                            const label = uf ? `${c.nome} - ${uf}` : c.nome;
                                            return (
                                                <Option key={c.id} value={c.id}>
                                                    {label}
                                                </Option>
                                            );
                                        })}
                                    </Select>
                                </Form.Item>
                            )}

                            <Form.Item
                                name="config_plano_proposta_comercial_id"
                                label="Selecione o Modelo de Plano"
                                rules={[{ required: true, message: 'Selecione um plano comercial' }]}
                            >
                                <Select
                                    placeholder="Selecione um modelo de plano configurado"
                                    onChange={handlePlanoChange}
                                    loading={loadingPlanos}
                                >
                                    {configPlanos.map(item => (
                                        <Option key={item.id} value={item.id}>
                                            {/* {item.is_principal ? '★ ' : ''} */}
                                            {item.plano?.nome || item.descricao_plano || `Plano #${item.id}`} - {formatarMoeda(Number(item.plano?.valor || 0))}
                                            {/* {item.is_principal ? ' (Principal)' : ''} */}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="quantidade_licencas"
                                        label="Quantidade de Licenças"
                                        rules={[{ required: true, message: 'Informe a quantidade de licenças' }]}
                                    >
                                        <InputNumber
                                            min={1}
                                            style={{ width: '100%' }}
                                            value={quantidadeLicencas}
                                            onChange={(val) => {
                                                const n = Number(val) || 1;
                                                setQuantidadeLicencas(n);
                                                form.setFieldsValue({ quantidade_licencas: n });
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                {contratacaoHoras && (
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="quantidade_horas"
                                            label={`Horas` /*de Consultoria / Suporte (${formatarMoeda(valorUnitarioHora)}/h)*/}
                                        >
                                            <InputNumber
                                                min={0}
                                                style={{ width: '100%' }}
                                                value={quantidadeHoras}
                                                onChange={(val) => {
                                                    const n = Number(val) || 0;
                                                    setQuantidadeHoras(n);
                                                    form.setFieldsValue({ quantidade_horas: n });
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                )}
                            </Row>

                            <Form.Item
                                name="condicao_pagamento"
                                label="Forma de Faturamento"
                                rules={[{ required: true, message: 'Selecione a forma de faturamento' }]}
                            >
                                <Radio.Group style={{ width: '100%' }}>
                                    <Space wrap size={[24, 10]} style={{ width: '100%' }}>
                                        <Radio value="parcela_unica">Parcela Única (Anual / À vista)</Radio>
                                        <Radio value="parcelado">Parcelado (Mensal)</Radio>
                                    </Space>
                                </Radio.Group>
                            </Form.Item>

                            {condicaoPagamentoModal === 'parcelado' && (
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="numero_parcelas"
                                            label="Quantidade de Parcelas"
                                            rules={[{ required: true, message: 'Informe a quantidade de parcelas' }]}
                                        >
                                            <InputNumber
                                                min={1}
                                                max={60}
                                                style={{ width: '100%' }}
                                                addonAfter="x"
                                                placeholder="12"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label="Valor por Parcela (Licenciamento)">
                                            <Input
                                                readOnly
                                                disabled
                                                value={`${Number(numeroParcelasModal || 12)}x de ${formatarMoeda(valorParcelaModal)}`}
                                                style={{
                                                    color: '#1890ff',
                                                    fontWeight: 600,
                                                    backgroundColor: isDark ? '#1f1f1f' : '#f0f5ff',
                                                    borderColor: '#91d5ff'
                                                }}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            )}

                            {contratacaoHoras && quantidadeHoras > 0 && (
                                <Form.Item
                                    name="detalhamento_horas"
                                    label="Detalhamento da Contratação de Horas"
                                    rules={[{ required: true, message: 'Informe o detalhamento da contratação de horas' }]}
                                    tooltip="Texto explicativo sobre como e quando as horas de desenvolvimento serão executadas e faturadas."
                                >
                                    <TextArea
                                        rows={3}
                                        placeholder="Informe o detalhamento da contratação de horas..."
                                    />
                                </Form.Item>
                            )}

                            <Form.Item
                                name="enviado_para"
                                label="Enviada para:"
                                tooltip="Se preenchido, a proposta já será criada com o status 'Enviada' e esse destinatário no histórico de envios."
                                style={{ marginBottom: 0 }}
                            >
                                <Input
                                    placeholder="Ex: Prefeito João Silva / Gabinete / pref@municipio.gov.br"
                                    allowClear
                                />
                            </Form.Item>
                        </Card>
                    </Form>
                </Spin>
            </Modal>

            {/* Modal de Pré-visualização Interativa dos Slides */}
            <VisualizadorPropostaModal
                visible={previewVisible}
                onClose={() => setPreviewVisible(false)}
                data={getPreviewData()}
                podeBaixarPdf={true}
            />

            {/* Documento Oculto para Renderização e Download Automático do PDF ao Salvar */}
            <PropostaPrintDocument ref={printRef} data={propostaParaDownload || getPreviewData()} />
        </>
    );
};

export default ModalCriarProposta;
